import React, { useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import GlobalStyles from 'styles/GlobalStyles';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import RestaurantLandingPage from 'Main.js';

import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Login from 'pages/Login';
import SignUp from 'pages/Signup';
import VerificarCuenta from 'pages/VerificarCuenta';
import AboutUs from 'pages/AboutUs';
import ContactUs from 'pages/ContactUs';
import Productos from 'pages/Productos';
import Viandas from 'pages/Viandas';
import PanelAdministrador from 'pages/PanelAdministrador';
import PaginaProducto from 'pages/PaginaProducto';
import Carrito from 'pages/Carrito';
import PaginaPack from 'pages/PaginaPack';
import { obtenerCategorias } from 'scripts/obtenerCategorias';
import { obtenerViandas } from 'scripts/obtenerViandas';
import { useDispatch, useSelector } from 'react-redux';
import { obtenerPedidoEnProgreso } from 'scripts/obtenerPedidoEnProgreso';
import ScrollToTop from 'helpers/ScrollToTop';
import { limpiarCarrito } from 'store/carritoSlice';
import PanelCocineras from 'pages/PanelCocineras';
import PaginaPedidos from 'pages/PaginaPedidos';
import PanelCadetes from 'pages/PanelCadete';
import RecuperarPassword from 'pages/RecuperarPassword';
import ResetearPassword from 'pages/ResetearPassword';
import CrearPedidoManual from 'pages/CrearPedidoManual';
import Perfil from 'pages/Perfil';
import Envios from 'pages/Envios';
import WhatsAppButton from './components/Widget/WhatsAppButton';
import PublicLayout from 'layouts/PublicLayout';
import HeaderOnlyLayout from 'layouts/HeaderOnlyLayout';
import { captureError, syncSentryUserContext } from 'utils/sentry';
import { carritoEstaExpirado, getCarritoExpiraEn } from 'utils/carritoExpiracion';
import { viandasCargadas, viandasCargando, viandasError } from 'store/viandaSlice';

const ROLES_VALIDOS = [1, 4, 5];

const theme = createTheme({
	palette: {
		grey: {
			200: '#f5f5f5',
			500: '#9e9e9e',
			700: '#616161',
		},
	},
});

export default function App() {
	const dispatch = useDispatch();
	const user = useSelector((state) => state.usuario.userInfo);
	const carrito = useSelector((state) => state.carrito);
	const token = sessionStorage.getItem('token');

	useEffect(() => {
		if (carrito.cantidadProductos > 0 && carritoEstaExpirado(carrito.primerProductoAgregadoAt)) {
			dispatch(limpiarCarrito());
			return undefined;
		}

		const expiraEn = getCarritoExpiraEn(carrito.primerProductoAgregadoAt);
		if (!expiraEn || carrito.cantidadProductos < 1) {
			return undefined;
		}

		const timeoutId = window.setTimeout(() => {
			dispatch(limpiarCarrito());
		}, Math.max(expiraEn - Date.now(), 0));

		return () => window.clearTimeout(timeoutId);
	}, [carrito.cantidadProductos, carrito.primerProductoAgregadoAt, dispatch]);

	useEffect(() => {
		const fetchViandas = async () => {
			dispatch(viandasCargando());
			try {
				const viandasData = await obtenerViandas();
				const viandas = Array.isArray(viandasData) ? viandasData : viandasData?.viandas || [];
				dispatch(viandasCargadas(viandas));
			} catch (error) {
				dispatch(viandasError(error.message));
				captureError(error, {
					tags: {
						error_source: 'app_bootstrap',
						operation: 'obtenerViandas',
					},
				});
			}
		};

		fetchViandas();
	}, [dispatch]);

	useEffect(() => {
		try {
			const fetchCategorias = async () => {
				await obtenerCategorias(dispatch).catch((e) => {
					toast.error('Error en el Obtener Categorias');
				});
			};
			fetchCategorias();

			if (user && ROLES_VALIDOS.includes(user.rol_id) && token) {
				const fetchPedido = async () => {
					await obtenerPedidoEnProgreso(user.id, token, dispatch).catch((e) => {
						captureError(e, {
							tags: {
								error_source: 'app_bootstrap',
								operation: 'obtenerPedidoEnProgreso',
							},
						});
						console.log('Error en el Obtener Pedido:' + e);
						dispatch(limpiarCarrito());
					});
				};
				fetchPedido();
			} else {
				dispatch(limpiarCarrito());
			}
		} catch (e) {
			captureError(e, {
				tags: {
					error_source: 'app_bootstrap',
				},
			});
			console.log(e);
			toast.error('Error al cargar la página. Por favor, recargue la página.');
		}
	}, [dispatch, user, token]);

	useEffect(() => {
		syncSentryUserContext(user);
	}, [user]);

	return (
		<>
			<ToastContainer
				position="top-right"
				autoClose={5000}
				hideProgressBar={true}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover={false}
				theme="light"
			/>
			<WhatsAppButton />
			<GlobalStyles />
			<ThemeProvider theme={theme}>
				<Router>
					<ScrollToTop />
					<Routes>
						<Route element={<PublicLayout />}>
							<Route index element={<RestaurantLandingPage />} />
							<Route path="nosotros" element={<AboutUs />} />
							<Route path="contacto" element={<ContactUs />} />
							<Route path="envios" element={<Envios />} />
							<Route path="productos" element={<Productos />} />
							<Route path="viandas" element={<Viandas />} />
							<Route path="producto/:id" element={<PaginaProducto />} />
							<Route path="pack/:id" element={<PaginaPack />} />
							<Route path="carrito" element={<Carrito />} />
							<Route path="mis_pedidos" element={<PaginaPedidos />} />
						</Route>
						<Route element={<HeaderOnlyLayout />}>
							<Route path="panelcocineras" element={<PanelCocineras />} />
							<Route path="panelcadetes" element={<PanelCadetes />} />
							<Route path="perfil" element={<Perfil />} />
						</Route>
						<Route path="/login" element={<Login />} />
						<Route path="/forgot-password" element={<RecuperarPassword />} />
						<Route path="/reset-password" element={<ResetearPassword />} />
						<Route path="/signup" element={<SignUp />} />
						<Route path="/verificar" element={<VerificarCuenta />} />
						<Route path="/paneladministrador" element={<PanelAdministrador />} />
						<Route path="/crear-pedido-manual" element={<CrearPedidoManual />} />
					</Routes>
				</Router>
			</ThemeProvider>
		</>
	);
}
